import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CalendarDays, Video, Download, Award, Bell, RefreshCcw } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Empty, Spinner } from "@/components/site/Bits";
import { useApi, useDownload, errMsg, fmtDate, fmtDateTime, fmtPrice, fmtTime, STATUS, fileUrl, setToken } from "@/lib/api";
import { useMe, usePageTitle } from "@/lib/site";

export const Badge = ({ s, testId }) => {
  const x = STATUS[s] || { label: s, cls: "bg-stone-100" };
  return <span data-testid={testId} className={`text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap ${x.cls}`}>{x.label}</span>;
};

export const VerifyNotice = ({ testId = "verify-email-notice" }) => {
  const { me, reloadMe } = useMe();
  const api = useApi();
  const [busy, setBusy] = useState(false);
  if (!me || me.email_verified) return null;
  const resend = async () => {
    setBusy(true);
    try { await api("post", "/auth/resend-verification"); toast.success("أرسلنا رابط التأكيد إلى بريدك"); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl border border-amber-300 bg-amber-50 p-5 flex flex-wrap items-center justify-between gap-3" data-testid={testId}>
      <div>
        <p className="font-semibold text-amber-900">يجب تأكيد بريدك الإلكتروني</p>
        <p className="text-sm text-amber-800 mt-1">افتح رابط التأكيد المرسل إلى <b dir="ltr">{me.email}</b> (تحقق من الرسائل غير المرغوبة). لا يمكن التسجيل في الدورات قبل تأكيد البريد. إن لم تصلك الرسالة تواصل مع إدارة الأكاديمية.</p>
      </div>
      <div className="flex gap-2">
        <button onClick={resend} disabled={busy} className="btn-copper !py-2 !px-4 text-sm" data-testid={`${testId}-resend`}>{busy ? "جارٍ الإرسال..." : "إعادة إرسال الرابط"}</button>
        <button onClick={reloadMe} className="btn-gold-outline !py-2 !px-4 text-sm" data-testid={`${testId}-refresh`}>أكدت بريدي</button>
      </div>
    </div>
  );
};

const Profile = () => {
  const { me, reloadMe } = useMe();
  const api = useApi();
  const [f, setF] = useState({ name: me?.name || "", phone: me?.phone || "" });
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const save = async (e) => {
    e.preventDefault();
    try { await api("put", "/me", f); toast.success("تم حفظ بياناتك"); reloadMe(); } catch (err) { toast.error(errMsg(err)); }
  };
  const changePw = async (e) => {
    e.preventDefault();
    try {
      const r = await api("post", "/auth/change-password", pw);
      setToken(r.token);
      setPw({ current_password: "", new_password: "" });
      toast.success("تم تغيير كلمة المرور");
    } catch (err) { toast.error(errMsg(err)); }
  };
  const resend = async () => {
    try { await api("post", "/auth/resend-verification"); toast.success("أرسلنا رابط التأكيد إلى بريدك"); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div className="grid lg:grid-cols-2 gap-6">
    <form onSubmit={save} className="card-lux !transform-none p-7 space-y-4" data-testid="account-profile-form">
      <div className="space-y-2"><Label>الاسم</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="profile-name-input" /></div>
      <div className="space-y-2"><Label>رقم الهاتف</Label><Input dir="ltr" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="profile-phone-input" /></div>
      <div className="space-y-2"><Label>البريد الإلكتروني</Label><Input dir="ltr" value={me?.email || ""} disabled data-testid="profile-email" /></div>
      {me?.email_verified ? (
        <p className="text-xs text-emerald-700" data-testid="email-verified-badge">البريد مؤكد</p>
      ) : (
        <p className="text-xs text-amber-700" data-testid="email-unverified-badge">البريد غير مؤكد — <button type="button" onClick={resend} className="underline" data-testid="resend-verification-btn">إعادة إرسال رابط التأكيد</button></p>
      )}
      <button className="btn-copper" data-testid="profile-save-btn">حفظ</button>
    </form>
    <form onSubmit={changePw} className="card-lux !transform-none p-7 space-y-4 h-fit" data-testid="change-password-form">
      <h3 className="font-display font-bold">تغيير كلمة المرور</h3>
      <div className="space-y-2"><Label>كلمة المرور الحالية</Label><Input type="password" dir="ltr" required value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} data-testid="current-password-input" /></div>
      <div className="space-y-2"><Label>كلمة المرور الجديدة (6 خانات أو أكثر)</Label><Input type="password" dir="ltr" required minLength={6} value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} data-testid="new-password-input" /></div>
      <button className="btn-gold-outline" data-testid="change-password-btn">تغيير كلمة المرور</button>
    </form>
    </div>
  );
};

const MyCourses = ({ items, onRetry, onMaterial }) =>
  !items.length ? <Empty text={<>لم تسجل في أي دورة بعد. <Link to="/courses" className="text-[var(--copper)] font-semibold">تصفح الدورات</Link></>} /> : (
    <div className="grid gap-5 md:grid-cols-2">
      {items.map((e) => (
        <div key={e.id} className="card-lux !transform-none overflow-hidden" data-testid={`my-enrollment-${e.reg_no}`}>
          <div className="flex gap-4 p-5">
            <img src={fileUrl(e.course.image) || "/images/course-ai.jpg"} alt="" className="w-24 h-24 rounded-xl object-cover shrink-0" />
            <div className="space-y-2 min-w-0">
              <h3 className="font-display font-bold leading-snug line-clamp-2">{e.course.title}</h3>
              <p className="text-xs text-[var(--muted-ink)]">رقم التسجيل: <b dir="ltr">{e.reg_no}</b></p>
              <Badge s={e.status} testId={`my-enrollment-status-${e.reg_no}`} />
            </div>
          </div>
          <div className="border-t border-[var(--line)] p-5 space-y-3 text-sm">
            <p className="flex items-center gap-2"><CalendarDays size={16} className="text-[var(--copper)]" />{fmtDate(e.course.date)} — {fmtTime(e.course.meet_time || e.course.time)}</p>
            {e.status === "confirmed" && (e.course.meet_link
              ? <a href={e.course.meet_link} target="_blank" rel="noreferrer" className="btn-copper !py-2 text-sm" data-testid={`my-meet-link-${e.reg_no}`}><Video size={16} />دخول اجتماع Google Meet</a>
              : <p className="text-[var(--muted-ink)]" data-testid={`my-meet-pending-${e.reg_no}`}>سيظهر رابط الاجتماع هنا قبل موعد الدورة.</p>)}
            {e.status === "confirmed" && e.course.material && (
              <button onClick={() => onMaterial(e)} className="btn-gold-outline !py-2 text-sm" data-testid={`my-material-${e.reg_no}`}><Download size={15} />تحميل ملف الدورة (PDF)</button>
            )}
            {e.status !== "confirmed" && (
              <button onClick={() => onRetry(e.id)} className="btn-gold-outline !py-2 text-sm" data-testid={`my-retry-pay-${e.reg_no}`}><RefreshCcw size={15} />إكمال / إعادة محاولة الدفع</button>
            )}
            {e.attendance && <p className="text-xs">الحضور: {e.attendance === "present" ? "حاضر" : "غائب"}</p>}
          </div>
        </div>
      ))}
    </div>
  );

const Table = ({ head, rows, testId }) => (
  <div className="card-lux !transform-none overflow-x-auto" data-testid={testId}>
    <table className="w-full text-sm">
      <thead className="bg-[var(--paper)]"><tr>{head.map((h) => <th key={h} className="text-right p-3 font-semibold whitespace-nowrap">{h}</th>)}</tr></thead>
      <tbody>{rows}</tbody>
    </table>
  </div>
);

export default function Account() {
  usePageTitle("حسابي");
  const api = useApi();
  const download = useDownload();
  const { me } = useMe();
  const [d, setD] = useState(null);
  const load = useCallback(async () => {
    const [enr, pays, certs, notes] = await Promise.all(["/my/enrollments", "/my/payments", "/my/certificates", "/my/notifications"].map((u) => api("get", u)));
    setD({ enr, pays, certs, notes });
  }, [api]);
  useEffect(() => {
    load().catch((e) => toast.error(errMsg(e)));
  }, [load]);
  const retry = async (id) => {
    try { const r = await api("post", `/enrollments/${id}/retry`); window.location.href = r.checkout_url; } catch (e) { toast.error(errMsg(e)); }
  };
  const dl = (url, name) => download(url, name).catch(() => toast.error("تعذر التحميل"));
  if (!d) return <Spinner />;
  return (
    <div className="container-x py-12" data-testid="account-page">
      <p className="eyebrow">حساب المتدرب</p>
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold mt-1 mb-8">أهلاً {me?.name || ""}</h1>
      <div className="mb-6"><VerifyNotice testId="account-verify-notice" /></div>
      <Tabs defaultValue="courses" dir="rtl">
        <TabsList className="flex-wrap h-auto bg-white border border-[var(--line)] p-1 mb-6">
          <TabsTrigger value="courses" data-testid="account-tab-courses">دوراتي</TabsTrigger>
          <TabsTrigger value="payments" data-testid="account-tab-payments">المدفوعات</TabsTrigger>
          <TabsTrigger value="certs" data-testid="account-tab-certs">الشهادات</TabsTrigger>
          <TabsTrigger value="notes" data-testid="account-tab-notes">الإشعارات</TabsTrigger>
          <TabsTrigger value="profile" data-testid="account-tab-profile">بياناتي</TabsTrigger>
        </TabsList>
        <TabsContent value="courses"><MyCourses items={d.enr} onRetry={retry} onMaterial={(e) => dl(`/courses/${e.course.id}/material`, e.course.material.filename)} /></TabsContent>
        <TabsContent value="payments">
          {!d.pays.length ? <Empty text="لا توجد مدفوعات" /> : (
            <Table testId="account-payments-table" head={["المرجع", "الدورة", "المبلغ", "الحالة", "التاريخ", "الإيصال"]} rows={d.pays.map((p) => (
              <tr key={p.id} className="border-t border-[var(--line)]">
                <td className="p-3 font-mono text-xs" dir="ltr">{p.ref}</td><td className="p-3 max-w-[240px] truncate">{p.course_title}</td>
                <td className="p-3 whitespace-nowrap">{fmtPrice(p.amount)}</td><td className="p-3"><Badge s={p.status} /></td>
                <td className="p-3 whitespace-nowrap">{fmtDateTime(p.paid_at || p.created_at)}</td>
                <td className="p-3">{p.status === "paid" && <button onClick={() => dl(`/payments/${p.ref}/receipt`, `receipt-${p.ref}.pdf`)} className="text-[var(--copper)] inline-flex items-center gap-1" data-testid={`receipt-download-${p.ref}`}><Download size={15} />تحميل</button>}</td>
              </tr>
            ))} />
          )}
        </TabsContent>
        <TabsContent value="certs">
          {!d.certs.length ? <Empty text="لا توجد شهادات بعد. تُصدر الشهادة بعد حضور الدورة." /> : (
            <div className="grid gap-5 md:grid-cols-2">{d.certs.map((c) => (
              <div key={c.id} className="card-lux !transform-none p-6 flex items-center gap-4" data-testid={`my-cert-${c.cert_no}`}>
                <Award size={40} className="text-[var(--gold)] shrink-0" />
                <div className="flex-1 min-w-0"><p className="font-display font-bold line-clamp-2">{c.course_title}</p><p className="text-xs text-[var(--muted-ink)] mt-1" dir="ltr">{c.cert_no}</p></div>
                <button onClick={() => dl(`/certificates/${c.cert_no}/pdf`, `certificate-${c.cert_no}.pdf`)} className="btn-copper !py-2 !px-4 text-sm" data-testid={`cert-download-${c.cert_no}`}><Download size={15} />PDF</button>
              </div>
            ))}</div>
          )}
        </TabsContent>
        <TabsContent value="notes">
          {!d.notes.length ? <Empty text="لا توجد إشعارات" /> : (
            <div className="space-y-3" data-testid="account-notifications">{d.notes.map((n) => (
              <div key={n.id} className="card-lux !transform-none p-5 flex gap-3"><Bell size={18} className="text-[var(--copper)] shrink-0 mt-1" />
                <div><p className="font-semibold">{n.title}</p><p className="text-sm text-[var(--muted-ink)] whitespace-pre-line mt-1">{n.body}</p><p className="text-xs text-[var(--muted-ink)] mt-2">{fmtDateTime(n.created_at)}</p></div>
              </div>
            ))}</div>
          )}
        </TabsContent>
        <TabsContent value="profile"><Profile /></TabsContent>
      </Tabs>
    </div>
  );
}
